import { COLUNAS_PRODUTOS, FATURA, PRODUTOS, TIPOGRAFIA } from './grade';
import { estimarAltura } from './metricaTexto';

/**
 * @file ./views/layouts/danfe/producao/paginacao.js
 *
 * Motor de paginação do quadro "Dados dos Produtos/Serviços" — o único de
 * altura variável do documento.
 *
 * Decide, antes de qualquer renderização, quantas folhas o DANFE terá, qual
 * item entra em qual folha e em que coordenada cada linha é desenhada. É
 * puro (sem React, sem renderer), o que o torna testável isoladamente.
 *
 * ## O que o MOC exige daqui
 *
 * O item 3.5 do Anexo II obriga toda folha adicional a repetir, na parte
 * superior e na mesma disposição da primeira: dados do emitente, as
 * descrições "DANFE" e "Documento Auxiliar da Nota Fiscal Eletrônica",
 * número, série, tipo de operação, ordem e total de folhas, código de
 * barras, natureza da operação, chave de acesso e as inscrições do
 * emitente. O item 3.10.2 exige o número da folha impresso na parte
 * superior de **todas** as folhas, inclusive quando há uma só.
 *
 * A área restante das folhas adicionais só pode receber os itens que não
 * couberam e as informações complementares — nada de repetir cálculo do
 * imposto ou transportador, e é o que este módulo determina ao marcar
 * `primeira`.
 */

/** Largura interna da coluna de descrição, onde a quebra de linha acontece. */
const LARGURA_DESCRICAO =
  COLUNAS_PRODUTOS.find((coluna) => coluna.chave === 'descricao').largura -
  PRODUTOS.recuoHorizontal * 2;

/**
 * Altura vertical consumida pelo quadro de fatura/duplicatas.
 *
 * O quadro cresce em bandas de 23 pt, sete duplicatas por banda, e empurra
 * para baixo tudo o que vem depois — cálculo do imposto, transportador e o
 * início da tabela de produtos. Sem duplicatas o quadro some por inteiro,
 * o que o item 3.3.2 do MOC permite explicitamente, desde que o espaço
 * liberado seja devolvido ao quadro de produtos (é o que acontece: o topo
 * da tabela sobe junto).
 *
 * @param {Array<Object>} duplicatas
 * @returns {number} Deslocamento em pontos.
 */
export function deslocamentoFatura(duplicatas) {
  const quantidade = (duplicatas ?? []).length;
  if (quantidade === 0) return 0;

  const bandas = Math.ceil(quantidade / FATURA.porBanda);
  return 7 + bandas * FATURA.alturaBanda;
}

/**
 * Altura de uma linha de item.
 *
 * Reproduz `Math.max(15, doc.heightOfString(texto, { width: 138 }) + 10)` do
 * gerador original, trocando a medição real do pdfkit pela estimativa de
 * `metricaTexto` — ver a justificativa lá.
 *
 * A descrição complementar (`infAdProd`) entra no cálculo porque o item
 * 3.1.7 do MOC manda imprimi-la logo abaixo do item a que se refere.
 *
 * @param {Object} item - Item já mapeado pelo `danfeMapper`.
 * @returns {number} Altura em pontos.
 */
export function alturaItem(item) {
  const alturaTexto = estimarAltura(
    item?.descricaoCompleta,
    LARGURA_DESCRICAO,
    TIPOGRAFIA.conteudoProduto,
  );

  const altura = Math.max(
    PRODUTOS.alturaPadraoLinha,
    alturaTexto + PRODUTOS.folgaVerticalLinha,
  );

  // Uma altura não-finita se propaga pelo cursor e faz o teste de quebra
  // sempre falhar, empilhando todos os itens numa folha só — falha que
  // não gera erro e só aparece no PDF pronto. Já aconteceu uma vez, ao
  // renomear uma propriedade da grade.
  if (!Number.isFinite(altura)) {
    throw new Error(
      `[DANFE/paginacao] Altura inválida para o item ${item?.numero ?? '?'}: ` +
        `verifique se a coluna de descrição tem largura definida na grade.`,
    );
  }

  return altura;
}

/**
 * Distribui os itens entre as folhas, já resolvendo a coordenada vertical
 * de cada linha.
 *
 * Diferença consciente em relação ao original: a quebra de folha compara o
 * **rodapé** da linha com o limite, e não o topo. O código de origem testava
 * `y + alturaPadrao > limite` usando sempre a altura padrão de 15 pt, então
 * um item de descrição longa começava dentro da área útil e terminava por
 * cima do quadro de dados adicionais.
 *
 * @param {Array<Object>} itens - Itens já mapeados.
 * @param {Array<Object>} duplicatas - Duplicatas, para o deslocamento da fatura.
 * @returns {{ deslocamentoFatura: number, folhas: Array<Object> }}
 *   Cada folha traz `numero`, `total`, `primeira`, `topoTitulo`,
 *   `topoTabela` e `linhas` (`{ item, y, altura }`).
 */
export function distribuirItensEmFolhas(itens, duplicatas = []) {
  const deslocamento = deslocamentoFatura(duplicatas);

  const abrirPrimeira = () => ({
    primeira: true,
    topoTitulo: PRODUTOS.primeiraFolha.titulo + deslocamento,
    topoTabela: PRODUTOS.primeiraFolha.tabela + deslocamento,
    linhas: [],
  });

  const abrirContinuacao = () => ({
    primeira: false,
    topoTitulo: PRODUTOS.continuacao.titulo,
    topoTabela: PRODUTOS.continuacao.tabela,
    linhas: [],
  });

  const folhas = [];
  let folha = abrirPrimeira();
  let cursor = folha.topoTabela;

  for (const item of itens ?? []) {
    const altura = alturaItem(item);
    const topoLinha = cursor + PRODUTOS.alturaCabecalho;

    // A primeira linha de uma folha entra sempre, mesmo que seja mais
    // alta que a área útil: adiá-la só repetiria o teste na folha
    // seguinte e travaria a paginação.
    const estouraFolha = topoLinha + altura > PRODUTOS.limiteQuebra;

    if (estouraFolha && folha.linhas.length > 0) {
      folhas.push(folha);
      folha = abrirContinuacao();
      cursor = folha.topoTabela;
    }

    folha.linhas.push({ item, y: cursor + PRODUTOS.alturaCabecalho, altura });
    cursor += altura;
  }

  folhas.push(folha);

  return {
    deslocamentoFatura: deslocamento,
    folhas: folhas.map((atual, indice) => ({
      ...atual,
      numero: indice + 1,
      total: folhas.length,
    })),
  };
}
